import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AddressBook, Clock, PaperPlaneRight, PencilSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { BuyerLogoRequest } from "@/components/buyer/BuyerLogoRequest";

const INPUT = "w-full border border-black px-3 py-2 text-sm mt-1 bg-white";
const BTN = "border-2 border-black px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40 transition-colors duration-100";
const AREA = new Set(["office_address"]);
const err = (e, f) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : f; };
const STATUS = { approved: "bg-emerald-100 border-emerald-800", rejected: "bg-rose-100 border-rose-800" };

function Changes({ changes, testId }) {
  return (
    <ul className="mt-1 space-y-0.5 text-[11px]" data-testid={testId}>
      {Object.entries(changes).map(([k, c]) => (
        <li key={k}><b>{c.label}:</b> <span className="text-zinc-500 line-through">{c.before || "—"}</span> → {c.after || "—"}</li>
      ))}
    </ul>
  );
}

function RequestForm({ data, onSent, onCancel }) {
  const start = { ...data.details, ...Object.fromEntries(Object.entries(data.pending?.changes || {}).map(([k, c]) => [k, c.after])) };
  const [form, setForm] = useState({ ...start, note: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/buyer/detail-requests", { ...form, contact_email: (form.contact_email || "").trim() || null });
      toast.success("Sent. You'll get an email once it's approved.");
      onSent();
    } catch (e) { toast.error(err(e, "Couldn't send your request")); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-3" data-testid="buyer-details-request-form">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {Object.entries(data.labels).map(([k, label]) => (
          <label key={k} className="label-mono">{label}
            {AREA.has(k)
              ? <textarea rows={2} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={INPUT} data-testid={`buyer-details-req-${k}`} />
              : <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={INPUT}
                       type={k === "contact_email" ? "email" : "text"} data-testid={`buyer-details-req-${k}`} />}
          </label>
        ))}
      </div>
      <label className="label-mono block">Note for us (optional)
        <textarea rows={2} maxLength={500} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={INPUT}
                  data-testid="buyer-details-req-note" />
      </label>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={send} disabled={busy} className={`${BTN} bg-black text-white`} data-testid="buyer-details-req-send">
          <PaperPlaneRight size={11} weight="bold" /> {busy ? "Sending…" : "Send for approval"}
        </button>
        <button type="button" onClick={onCancel} className={`${BTN} hover:bg-black hover:text-white`} data-testid="buyer-details-req-cancel">Cancel</button>
      </div>
    </div>
  );
}

// Buyer dashboard: their company + contact details, and a request to change them (an admin approves it).
export function BuyerDetailsRequest() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const load = useCallback(() => {
    api.get("/buyer/details").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <div className="mt-8 brut-card p-5" data-testid="buyer-details-card">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <AddressBook size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">Your company details</div>
        {!editing && (
          <button type="button" onClick={() => setEditing(true)} className={`${BTN} ml-auto hover:bg-black hover:text-white`} data-testid="buyer-details-request-btn">
            <PencilSimple size={11} weight="bold" /> Request a change
          </button>
        )}
      </div>
      <p className="mb-3 text-[11px] text-zinc-600">These show on all your landing and thank-you pages. Changes go live once we approve them.</p>
      {data.pending && (
        <div className="mb-3 border-2 border-amber-600 bg-amber-50 p-3 text-xs" data-testid="buyer-details-pending">
          <div className="flex items-center gap-1.5 font-bold"><Clock size={12} weight="bold" /> Waiting for approval · sent {new Date(data.pending.submitted_at).toLocaleString()}</div>
          <Changes changes={data.pending.changes} testId="buyer-details-pending-changes" />
        </div>
      )}
      {editing ? (
        <RequestForm data={data} onSent={() => { setEditing(false); load(); }} onCancel={() => setEditing(false)} />
      ) : (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-xs md:grid-cols-2" data-testid="buyer-details-current">
          {Object.entries(data.labels).map(([k, label]) => (
            <div key={k} className="flex flex-col gap-0.5 md:flex-row md:gap-2"><dt className="label-mono shrink-0 md:w-40">{label}</dt>
              <dd className="min-w-0 [overflow-wrap:anywhere]" data-testid={`buyer-details-current-${k}`}>{data.details[k] || "—"}</dd></div>
          ))}
        </dl>
      )}
      {data.logo && <BuyerLogoRequest logo={data.logo} onSent={load} />}
      {data.history.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="buyer-details-history">
          <div className="label-mono">Past requests</div>
          {data.history.map((r) => (
            <div key={r.id} className={`border-2 p-2 text-[11px] ${STATUS[r.status] || ""}`} data-testid={`buyer-details-history-${r.id}`}>
              <b className="uppercase">{r.status === "approved" ? "Approved" : "Not approved"}</b> · {new Date(r.reviewed_at || r.submitted_at).toLocaleDateString()}
              {r.review_note && <span> · {r.review_note}</span>}
              <Changes changes={r.changes} testId={`buyer-details-history-changes-${r.id}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
